import { useState, useRef } from 'react';
import { Gift, Check, Download, Search, Ticket } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_CHANCES = 3;

const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/verse.clinic/',
  instagram: 'https://instagram.com/verseclinic.my/',
};

/* Brand SVG icons */
const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const SOCIAL_ITEMS = [
  { key: 'facebook', label: 'Follow on Facebook', Icon: FacebookIcon },
  { key: 'instagram', label: 'Follow on Instagram', Icon: InstagramIcon },
];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/* ───── Shareable Card Generator ───── */
const generateCard = async (ticket, chances, name) => {
  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 480;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#071525';
  ctx.fillRect(0, 0, 800, 480);
  ctx.strokeStyle = '#d6a84a';
  ctx.lineWidth = 2;
  ctx.strokeRect(24, 24, 752, 432);
  ctx.strokeStyle = 'rgba(214,168,74,0.2)';
  ctx.lineWidth = 1;
  ctx.strokeRect(32, 32, 736, 416);

  ctx.fillStyle = '#d6a84a';
  ctx.font = 'bold 28px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('VERSE CLINIC', 400, 80);

  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.font = '12px Inter, sans-serif';
  ctx.fillText('LUCKY DRAW ENTRY', 400, 108);

  ctx.fillStyle = '#d6a84a';
  ctx.font = 'bold 56px Inter, sans-serif';
  ctx.fillText(ticket, 400, 195);

  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.font = '18px Inter, sans-serif';
  ctx.fillText(name, 400, 235);

  ctx.strokeStyle = 'rgba(214,168,74,0.3)';
  ctx.beginPath();
  ctx.moveTo(200, 265);
  ctx.lineTo(600, 265);
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 36px Inter, sans-serif';
  ctx.fillText(`${chances}/${MAX_CHANCES} CHANCES`, 400, 315);

  const dotY = 345;
  const dotSpacing = 36;
  const startX = 400 - ((MAX_CHANCES * dotSpacing) / 2) + dotSpacing / 2;
  for (let i = 0; i < MAX_CHANCES; i++) {
    ctx.beginPath();
    ctx.arc(startX + i * dotSpacing, dotY, 8, 0, Math.PI * 2);
    if (i < chances) {
      ctx.fillStyle = '#d6a84a';
      ctx.fill();
    } else {
      ctx.strokeStyle = 'rgba(214,168,74,0.3)';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = '14px Inter, sans-serif';
  ctx.fillText('Grand Opening  ·  Kuala Lumpur', 400, 400);

  ctx.fillStyle = 'rgba(214,168,74,0.4)';
  ctx.font = '11px Inter, sans-serif';
  ctx.fillText('verseclinic.com', 400, 435);

  const link = document.createElement('a');
  link.download = `${ticket}-lucky-draw.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
};

/* ───── Stacked Tickets Visual ───── */
const TicketStack = ({ count }) => (
  <div className="relative h-20 w-28 mx-auto" data-testid="ticket-stack">
    {[...Array(MAX_CHANCES)].map((_, i) => (
      <div
        key={i}
        className="absolute left-1/2 rounded-lg transition-all duration-500"
        style={{
          width: '88px',
          height: '56px',
          transform: `translateX(-50%) translateY(${i * -6}px) rotate(${(i - 1) * 4}deg)`,
          backgroundColor: i < count ? 'rgba(214,168,74,0.15)' : 'rgba(255,255,255,0.03)',
          border: `1px solid ${i < count ? 'rgba(214,168,74,0.4)' : 'rgba(255,255,255,0.06)'}`,
          zIndex: i,
          bottom: 0,
          opacity: i < count ? 1 : 0.4,
        }}
      >
        {i < count && (
          <div className="flex items-center justify-center h-full">
            <Ticket className="w-4 h-4" style={{ color: 'var(--vc-gold)' }} />
          </div>
        )}
      </div>
    ))}
  </div>
);

/* ───── Progress Bar ───── */
const ChanceProgress = ({ social, total }) => (
  <div className="space-y-2" data-testid="chance-progress">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>
        Chances Earned
      </span>
      <span className="text-sm font-bold" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
        {total}/{MAX_CHANCES}
      </span>
    </div>
    <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${(total / MAX_CHANCES) * 100}%`, backgroundColor: 'var(--vc-gold)', opacity: 0.85 }}
      />
    </div>
    <div className="flex items-center gap-3 justify-center mt-1">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--vc-gold)' }} />
      <span className="text-[10px]" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
        Base entry{social.facebook ? ' + Facebook' : ''}{social.instagram ? ' + Instagram' : ''}
      </span>
    </div>
  </div>
);

/* ───── Social Action Buttons ───── */
const SocialActions = ({ social, onAction, loading, doneLabel, chanceLabel }) => (
  <div className="space-y-2.5">
    {SOCIAL_ITEMS.map(({ key, label, Icon }) => {
      const done = social[key];
      return (
        <button
          key={key}
          data-testid={`social-action-${key}`}
          onClick={() => !done && onAction(key)}
          disabled={done || loading === key}
          className="w-full flex items-center gap-3 h-12 px-4 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.98]"
          style={{
            backgroundColor: done ? 'rgba(214,168,74,0.08)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${done ? 'rgba(214,168,74,0.25)' : 'rgba(255,255,255,0.08)'}`,
            color: done ? 'var(--vc-gold)' : 'var(--vc-fg-muted)',
            fontFamily: 'Inter, sans-serif',
            cursor: done ? 'default' : 'pointer',
          }}
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1 text-left">{label}</span>
          {done ? (
            <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--vc-gold)' }}>
              <Check className="w-4 h-4" /> {doneLabel || 'Done'}
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full" style={{ color: 'var(--vc-gold)', backgroundColor: 'rgba(214,168,74,0.1)' }}>{chanceLabel || '+1 Chance'}</span>
          )}
        </button>
      );
    })}
  </div>
);

/* ───── Lucky Draw Card (shown after signup) ───── */
export const LuckyDrawCard = ({ ticket, position, name, phone }) => {
  const { t } = useTranslation();
  const [social, setSocial] = useState({ facebook: false, instagram: false });
  const [total, setTotal] = useState(1);
  const [loading, setLoading] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const handleAction = (key) => {
    window.open(SOCIAL_LINKS[key], '_blank');
    setPendingAction(key);
  };

  const confirmAction = async () => {
    if (!pendingAction || !phone) return;
    setLoading(pendingAction);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lucky-draw/social-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, action: pendingAction }),
      });
      const data = await res.json();
      if (data.ok) {
        setSocial((prev) => ({ ...prev, [pendingAction]: true }));
        setTotal(data.total_chances);
      }
    } catch {
      // silently fail
    }
    setLoading(null);
    setPendingAction(null);
  };

  return (
    <div data-testid="lucky-draw-card" className="mt-8 rounded-[20px] p-6 sm:p-8" style={{
      backgroundColor: 'rgba(214,168,74,0.04)',
      border: '1px solid rgba(214,168,74,0.15)',
    }}>
      <div className="text-center mb-5">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4" style={{
          backgroundColor: 'rgba(214,168,74,0.1)', border: '1px solid rgba(214,168,74,0.2)',
        }}>
          <Gift className="w-3.5 h-3.5" style={{ color: 'var(--vc-gold)' }} />
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
            {t('lucky_draw.badge')}
          </span>
        </div>
        <p className="text-3xl font-bold tracking-tight" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
          {ticket}
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
          {t('lucky_draw.badge')}
        </p>
      </div>

      <TicketStack count={total} />

      <div className="my-5">
        <ChanceProgress social={social} total={total} />
      </div>

      <p className="text-xs font-medium mb-3 text-center" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>
        {t('lucky_draw.follow_extra')}
      </p>

      <SocialActions social={social} onAction={handleAction} loading={loading} doneLabel={t('lucky_draw.done_social')} chanceLabel={t('lucky_draw.plus_chance')} />

      {pendingAction && (
        <button
          data-testid="confirm-social-action"
          onClick={confirmAction}
          className="w-full mt-3 h-11 rounded-xl text-sm font-semibold transition-colors duration-200 active:scale-[0.98]"
          style={{ backgroundColor: 'var(--vc-gold)', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}
        >
          {t('lucky_draw.confirm_follow')}
        </button>
      )}

      <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <button
          data-testid="download-lucky-draw-card"
          onClick={() => generateCard(ticket, total, name)}
          className="w-full flex items-center justify-center gap-2 h-10 rounded-xl text-sm font-medium border transition-colors duration-200 active:scale-[0.98]"
          style={{
            borderColor: 'rgba(214,168,74,0.25)',
            color: 'var(--vc-gold)',
            backgroundColor: 'transparent',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          <Download className="w-3.5 h-3.5" />
          {t('lucky_draw.save_card')}
        </button>
      </div>

      <p className="text-[10px] text-center mt-3" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
        {t('lucky_draw.footer_notice')}
      </p>
    </div>
  );
};

/* ───── Check My Status Section ───── */
export const LuckyDrawStatus = () => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [socialLoading, setSocialLoading] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const inputRef = useRef(null);

  const lookup = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setLoading(true);
    setError('');
    setStatus(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lucky-draw/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await res.json();
      if (data.ok) {
        setStatus(data);
      } else {
        setError(data.message || t('lucky_draw.no_entry'));
      }
    } catch {
      setError(t('lucky_draw.connection_error'));
    }
    setLoading(false);
  };

  const handleSocialAction = (key) => {
    window.open(SOCIAL_LINKS[key], '_blank');
    setPendingAction(key);
  };

  const confirmSocialAction = async () => {
    if (!pendingAction) return;
    setSocialLoading(pendingAction);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lucky-draw/social-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), action: pendingAction }),
      });
      const data = await res.json();
      if (data.ok) {
        setStatus((prev) => ({
          ...prev,
          social_actions: { ...prev.social_actions, [pendingAction]: true },
          total_chances: data.total_chances,
        }));
      }
    } catch {
      // silently fail
    }
    setSocialLoading(null);
    setPendingAction(null);
  };

  return (
    <section data-testid="lucky-draw-status-section" className="py-12 lg:py-16 px-6 sm:px-8" style={{ backgroundColor: 'var(--vc-bg)' }}>
      <div className="max-w-lg mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5" style={{
          backgroundColor: 'rgba(214,168,74,0.08)', border: '1px solid rgba(214,168,74,0.2)',
        }}>
          <Gift className="w-3.5 h-3.5" style={{ color: 'var(--vc-gold)' }} />
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
            {t('lucky_draw.badge')}
          </span>
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-fg)' }}>
          {t('lucky_draw.check_title')}
        </h2>
        <p className="text-sm mb-8" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>
          {t('lucky_draw.check_subtitle')}
        </p>

        <form onSubmit={lookup} className="flex gap-2 mb-6">
          <input
            ref={inputRef}
            data-testid="lucky-draw-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+60 12 345 6789"
            className="flex-1 h-12 px-4 rounded-xl border outline-none transition-colors duration-200 text-sm"
            style={{
              backgroundColor: 'rgba(255,255,255,0.04)',
              borderColor: 'rgba(255,255,255,0.1)',
              color: 'var(--vc-fg)',
              fontFamily: 'Inter, sans-serif',
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgba(214,168,74,0.4)'}
            onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
          />
          <button
            data-testid="lucky-draw-lookup-btn"
            type="submit"
            disabled={loading}
            className="h-12 px-6 rounded-xl font-medium text-sm transition-colors duration-200 active:scale-[0.98] flex items-center gap-2"
            style={{ backgroundColor: 'var(--vc-gold)', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}
          >
            <Search className="w-4 h-4" />
            {loading ? t('lucky_draw.searching') : t('lucky_draw.lookup')}
          </button>
        </form>

        {error && (
          <p data-testid="lucky-draw-error" className="text-sm mb-4" style={{ color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>{error}</p>
        )}

        {status && (
          <div data-testid="lucky-draw-result" className="rounded-[20px] p-6 sm:p-8 text-center" style={{
            backgroundColor: 'rgba(214,168,74,0.04)',
            border: '1px solid rgba(214,168,74,0.15)',
          }}>
            <p className="text-xs mb-1" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
              Welcome back, {status.name}
            </p>
            <p className="text-3xl font-bold tracking-tight mb-1" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
              {status.lucky_draw_ticket}
            </p>
            <p className="text-xs mb-5" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
              Your Lucky Draw Ticket
            </p>

            <TicketStack count={status.total_chances} />

            <div className="my-5">
              <ChanceProgress social={status.social_actions} total={status.total_chances} />
            </div>

            {status.total_chances < MAX_CHANCES && (
              <>
                <p className="text-xs font-medium mb-3" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>
                  Earn more chances:
                </p>
                <SocialActions social={status.social_actions} onAction={handleSocialAction} loading={socialLoading} />
                {pendingAction && (
                  <button
                    data-testid="confirm-social-lookup"
                    onClick={confirmSocialAction}
                    className="w-full mt-3 h-11 rounded-xl text-sm font-semibold transition-colors duration-200 active:scale-[0.98]"
                    style={{ backgroundColor: 'var(--vc-gold)', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('lucky_draw.confirm_follow')}
                  </button>
                )}
              </>
            )}

            <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button
                data-testid="download-card-lookup"
                onClick={() => generateCard(status.lucky_draw_ticket, status.total_chances, status.name)}
                className="w-full flex items-center justify-center gap-2 h-10 rounded-xl text-sm font-medium border transition-colors duration-200 active:scale-[0.98]"
                style={{
                  borderColor: 'rgba(214,168,74,0.25)',
                  color: 'var(--vc-gold)',
                  backgroundColor: 'transparent',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                <Download className="w-3.5 h-3.5" />
                {t('lucky_draw.save_card')}
              </button>
            </div>

            <p className="text-[10px] mt-3" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
              Ensure your phone number is valid — winner will be contacted via WhatsApp
            </p>
          </div>
        )}

        {!status && !error && (
          <p className="text-xs" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
            {t('lucky_draw.join_now')}{' '}
            <button onClick={() => scrollToSection('waitlist')} className="underline" style={{ color: 'var(--vc-gold)' }}>
              {t('lucky_draw.join_now_link')}
            </button>{' '}
            {t('lucky_draw.join_to_get')}
          </p>
        )}
      </div>
    </section>
  );
};


/* ───── Compact Status Lookup (for sidebar) ───── */
export const LuckyDrawStatusCompact = () => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const lookup = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setLoading(true);
    setError('');
    setStatus(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lucky-draw/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await res.json();
      if (data.ok) setStatus(data);
      else setError(data.message || t('lucky_draw.no_entry'));
    } catch {
      setError(t('lucky_draw.connection_error_short'));
    }
    setLoading(false);
  };

  return (
    <div data-testid="lucky-draw-status-compact" className="rounded-[20px] p-6" style={{
      backgroundColor: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.06)',
    }}>
      <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>
        {t('lucky_draw.already_on_waitlist')}
      </p>
      <form onSubmit={lookup} className="flex gap-2">
        <input
          data-testid="compact-status-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+60 12 345 6789"
          className="flex-1 h-10 px-3 rounded-lg border outline-none text-sm"
          style={{
            backgroundColor: 'rgba(255,255,255,0.04)',
            borderColor: 'rgba(255,255,255,0.1)',
            color: 'var(--vc-fg)',
            fontFamily: 'Inter, sans-serif',
            minWidth: 0,
          }}
          onFocus={(e) => e.target.style.borderColor = 'rgba(214,168,74,0.4)'}
          onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
        />
        <button
          data-testid="compact-status-btn"
          type="submit"
          disabled={loading}
          className="h-10 px-4 rounded-lg font-medium text-xs transition-colors duration-200 active:scale-[0.97] flex-shrink-0"
          style={{ backgroundColor: 'var(--vc-gold)', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}
        >
          <Search className="w-3.5 h-3.5" />
        </button>
      </form>

      {error && <p className="text-xs mt-2" style={{ color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>{error}</p>}

      {status && (
        <div className="mt-3 p-3 rounded-xl text-center" style={{
          backgroundColor: 'rgba(214,168,74,0.06)',
          border: '1px solid rgba(214,168,74,0.15)',
        }}>
          <p className="text-lg font-bold" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>{status.lucky_draw_ticket}</p>
          <div className="flex items-center justify-center gap-2 mt-1">
            <div className="flex gap-0.5">
              {[...Array(MAX_CHANCES)].map((_, i) => (
                <div key={i} className="w-6 h-1 rounded-full" style={{
                  backgroundColor: i < status.total_chances ? 'var(--vc-gold)' : 'rgba(214,168,74,0.15)',
                }} />
              ))}
            </div>
            <span className="text-xs font-medium" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>{status.total_chances}/{MAX_CHANCES}</span>
          </div>
        </div>
      )}
    </div>
  );
};
